/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { FormEvent, useEffect, useState } from "react";
import summaryStyle from "@/src/components/Form/Common/Style/summaryStyle";
import Grid from "@mui/material/Grid";
import { Link, useLocation, useNavigate } from "react-router";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import PersonalData from "./Summary/PersonalData";
import { formValue } from "@/src/services/formServiceFunctions";
import CategoryData from "./Summary/CategoryData";
import _get from "lodash/get";
import translations from "@/src/services/death/translations";
import Button from "@mui/material/Button";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import { toCheckout } from "@/src/services/checkoutService";
import EmailVerificationModal from "@/src/components/FormInfoModal/EmailVerificationModal";
import { getCaseLink } from "@/src/services/productService";
import { useForm } from "@/src/provider/Form/FormProvider";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import { requiredValidator } from "@/src/services/validationRules";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";

type SummaryProps = { product: AbstractCase };

export default function Summary({ product }: SummaryProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { values } = useForm();
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });

  const [isLoading, setIsLoading] = useState(false);

  const stateField = location.state?.field;

  useEffect(() => {
    const el = document.getElementById(stateField);
    el && el.scrollIntoView();
  }, [stateField]);

  const basePath = getCaseLink(product) + "/antrag";

  const translate = (path: string) => {
    return _get(translations, path);
  };

  const handleCheckout = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    await updateMutation.mutateAsync({
      uri: "/deaths",
      id: product.id,
      data: { personalTextMessage: formValue(values, "personalTextMessage") },
    });
    await toCheckout(product.checkoutItem.id + "_" + product.id, navigate);
  };

  return (
    <>
    <ContentBox>
        <Typography variant={"h5"}>
          Ihre Angaben
        </Typography>
        <Grid container>
          <CategoryData formData={values} translate={translate} />
          <PersonalData formData={values} product={product} />
        </Grid>
        <form onSubmit={handleCheckout}>
          <ValidatorTextField
            name="personalTextMessage"
            label="Ihr Anliegen"
            placeholder="Schildern Sie hier kurz Ihr Anliegen"
            InputLabelProps={{
              shrink: true,
            }}
            multiline
            rows={10}
            validators={[requiredValidator("Bitte schildern Sie uns hier kurz Ihr Anliegen.")]}
          />
          <Grid container>
            <Grid
              sx={summaryStyle.backButtonContainer}
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Button fullWidth variant={"text"} component={Link} to={basePath + "/kategorie"}>
                Alles bearbeiten
              </Button>
            </Grid>
            <Grid
              sx={summaryStyle.submitButtonContainer}
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <ButtonLoading fullWidth color={"primary"} variant={"contained"} type={"submit"} isLoading={isLoading} loadingText={"Dokument wird erstellt"}>
                Angaben speichern
              </ButtonLoading>
            </Grid>
          </Grid>
        </form>
        <EmailVerificationModal />
      </ContentBox>
    </>
  );
}
